import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ApiError } from '../../api/generated/core/ApiError';
import { GoogleReviewService } from '../../api/generated/services/GoogleReviewService';
import { GoogleReviewView as GoogleReviewViewModel } from '../../api/generated/models/GoogleReviewView';
import { GoogleReviewSyncStateView } from '../../api/generated/models/GoogleReviewSyncStateView';
import type {
  GoogleAccountView,
  GoogleConnectionStatusView,
  GoogleLocationView,
  GoogleReviewBulkPublishResultView,
  GoogleReviewListView,
  GoogleReviewOAuthConnectView,
  GoogleReviewView,
} from '../../types/googleReview';
import { helper } from '../../utils/helper';
import type { RootState } from '../store';

interface ReviewCounts {
  all: number;
  needsReply: number;
  draft: number;
  published: number;
}

interface GoogleReviewState {
  isConnected: boolean;
  accountEmail: string | null;
  needsReconnect: boolean;
  syncState: GoogleReviewSyncStateView | null;
  isSyncTriggering: boolean;
  accounts: GoogleAccountView[];
  locations: GoogleLocationView[];
  selectedAccountId: string | null;
  selectedLocationId: string | null;
  reviews: GoogleReviewView[];
  counts: ReviewCounts;
  total: number;
  page: number;
  isLoadingMore: boolean;
  activeTab: 'all' | 'needs_reply' | 'draft' | 'published';
  ratingFilter: number | null;
  includeRatingOnly: boolean;
  selectedReviewIds: string[];
  currentEditReview: GoogleReviewView | null;
  currentEditDraftText: string;
  bulkDrafts: Record<string, string>;
  isLoading: boolean;
  isGenerating: boolean;
  isPublishing: boolean;
  error: string | null;
  errorCode: string | null;
}

const EMPTY_COUNTS: ReviewCounts = { all: 0, needsReply: 0, draft: 0, published: 0 };

/** Reviews per page; the "Load more" button pulls the next page and appends. */
const REVIEWS_PAGE_SIZE = 20;

/** Server cap: POST /google-review/bulk-generate validates `reviewIds` at max 20
 *  (GoogleReviewBulkGenerateRequest). Enforced here too so the list appending across
 *  "Load more" can't push a selection past it and trigger a raw validator error. */
export const BULK_GENERATE_MAX = 20;

/** Single place that maps a server sync_state onto the flags the UI reads. */
function applySyncState(state: GoogleReviewState, sync: GoogleReviewSyncStateView | undefined): void {
  if (!sync) return;
  state.syncState = sync;
  // The server's needs_reconnect is the source of truth for the reconnect prompt now that reads
  // never touch Google (so a google_token_expired read error can no longer fire).
  state.needsReconnect = sync.syncStatus === GoogleReviewSyncStateView.syncStatus.NEEDS_RECONNECT;
}

/** Error codes that have a localized message; anything else falls back to the backend message. */
const GOOGLE_REVIEW_ERROR_I18N: Record<string, string> = {
  google_generation_limit_reached: 'google_review_error_generation_limit',
};

/** Resolve a user-facing error message, preferring a localized message for known error codes. */
export function googleReviewErrorMessage(
  errorCode: string | null | undefined,
  fallback: string | null,
  t: (key: string) => string,
): string {
  const key = errorCode ? GOOGLE_REVIEW_ERROR_I18N[errorCode] : undefined;
  return key ? t(key) : (fallback ?? '');
}

const initialState: GoogleReviewState = {
  isConnected: false,
  accountEmail: null,
  needsReconnect: false,
  syncState: null,
  isSyncTriggering: false,
  accounts: [],
  locations: [],
  selectedAccountId: null,
  selectedLocationId: null,
  reviews: [],
  counts: EMPTY_COUNTS,
  total: 0,
  page: 1,
  isLoadingMore: false,
  activeTab: 'all',
  ratingFilter: null,
  includeRatingOnly: true,
  selectedReviewIds: [],
  currentEditReview: null,
  currentEditDraftText: '',
  bulkDrafts: {},
  isLoading: false,
  isGenerating: false,
  isPublishing: false,
  error: null,
  errorCode: null,
};

interface GoogleReviewRejectPayload {
  message: string;
  errorCode?: string;
}

function parseGoogleReviewError(error: unknown): GoogleReviewRejectPayload {
  if (error instanceof ApiError) {
    const body = helper.parseApiErrorBody(error) as { error?: string; errorCode?: string } | null;
    return {
      message: body?.error ?? error.message,
      errorCode: body?.errorCode,
    };
  }
  if (error instanceof Error) {
    return { message: error.message };
  }
  return { message: 'Unknown error' };
}

function rejectGoogleReviewError<RejectReturn>(
  error: unknown,
  rejectWithValue: (value: GoogleReviewRejectPayload) => RejectReturn,
): RejectReturn {
  return rejectWithValue(parseGoogleReviewError(error));
}

export const fetchConnectionStatus = createAsyncThunk<
  GoogleConnectionStatusView,
  void,
  { rejectValue: GoogleReviewRejectPayload }
>(
  'googleReview/fetchConnectionStatus',
  async (_, { rejectWithValue }) => {
    try {
      return await GoogleReviewService.getAppGoogleReviewConnectionStatus();
    } catch (e) {
      return rejectGoogleReviewError(e, rejectWithValue);
    }
  },
);

export const getOAuthConnectUrl = createAsyncThunk<
  GoogleReviewOAuthConnectView,
  void,
  { rejectValue: GoogleReviewRejectPayload }
>(
  'googleReview/getOAuthConnectUrl',
  async (_, { rejectWithValue }) => {
    try {
      return await GoogleReviewService.getAppGoogleReviewOauthConnect();
    } catch (e) {
      return rejectGoogleReviewError(e, rejectWithValue);
    }
  },
);

export const fetchAccounts = createAsyncThunk<
  GoogleAccountView[],
  void,
  { rejectValue: GoogleReviewRejectPayload }
>(
  'googleReview/fetchAccounts',
  async (_, { rejectWithValue }) => {
    try {
      return await GoogleReviewService.getAppGoogleReviewAccounts();
    } catch (e) {
      return rejectGoogleReviewError(e, rejectWithValue);
    }
  },
);

export const fetchLocations = createAsyncThunk<
  GoogleLocationView[],
  string,
  { rejectValue: GoogleReviewRejectPayload }
>(
  'googleReview/fetchLocations',
  async (accountId: string, { rejectWithValue }) => {
    try {
      return await GoogleReviewService.getAppGoogleReviewLocations(accountId);
    } catch (e) {
      return rejectGoogleReviewError(e, rejectWithValue);
    }
  },
);

interface FetchReviewsParams {
  locationId: string;
  filter?: string;
  rating?: number | null;
  includeRatingOnly?: boolean;
  // Omitted or 1 = fresh load (replaces the list); > 1 = "Load more" (appends).
  page?: number;
}

export const fetchReviews = createAsyncThunk<
  GoogleReviewListView,
  FetchReviewsParams,
  { rejectValue: GoogleReviewRejectPayload }
>(
  'googleReview/fetchReviews',
  async (params: FetchReviewsParams, { rejectWithValue }) => {
    try {
      const filter = params.filter && params.filter !== 'all'
        ? params.filter as 'needs_reply' | 'draft' | 'published'
        : undefined;

      return await GoogleReviewService.getAppGoogleReviewReviews(
        params.locationId,
        filter,
        params.rating ?? undefined,
        params.includeRatingOnly ?? true,
        params.page ?? 1,
        REVIEWS_PAGE_SIZE,
      );
    } catch (e) {
      return rejectGoogleReviewError(e, rejectWithValue);
    }
  },
);

export const triggerSync = createAsyncThunk<
  GoogleReviewSyncStateView,
  void,
  { rejectValue: GoogleReviewRejectPayload }
>(
  'googleReview/triggerSync',
  async (_, { rejectWithValue }) => {
    try {
      return await GoogleReviewService.postAppGoogleReviewSync();
    } catch (e) {
      return rejectGoogleReviewError(e, rejectWithValue);
    }
  },
);

export const generateAiReply = createAsyncThunk<
  { reviewId: string; text: string },
  string,
  { rejectValue: GoogleReviewRejectPayload }
>(
  'googleReview/generateAiReply',
  async (reviewId: string, { getState, rejectWithValue }) => {
    const locationId = (getState() as RootState).googleReview.selectedLocationId;
    if (!locationId) {
      return rejectWithValue({ message: 'No location selected' });
    }

    try {
      const result = await GoogleReviewService.postAppGoogleReviewGenerateReply({
        reviewId,
        locationId,
      });
      return { reviewId, text: result.text ?? '' };
    } catch (e) {
      return rejectGoogleReviewError(e, rejectWithValue);
    }
  },
);

export const saveDraft = createAsyncThunk<
  { reviewId: string; text: string },
  { reviewId: string; text: string },
  { rejectValue: GoogleReviewRejectPayload }
>(
  'googleReview/saveDraft',
  async ({ reviewId, text }: { reviewId: string; text: string }, { getState, rejectWithValue }) => {
    const locationId = (getState() as RootState).googleReview.selectedLocationId;
    if (!locationId) {
      return rejectWithValue({ message: 'No location selected' });
    }

    try {
      await GoogleReviewService.postAppGoogleReviewSaveDraft({
        reviewId,
        locationId,
        text,
      });
      return { reviewId, text };
    } catch (e) {
      return rejectGoogleReviewError(e, rejectWithValue);
    }
  },
);

export const publishReply = createAsyncThunk<
  { reviewId: string; text: string },
  { reviewId: string; text: string },
  { rejectValue: GoogleReviewRejectPayload }
>(
  'googleReview/publishReply',
  async ({ reviewId, text }: { reviewId: string; text: string }, { getState, rejectWithValue }) => {
    const locationId = (getState() as RootState).googleReview.selectedLocationId;
    if (!locationId) {
      return rejectWithValue({ message: 'No location selected' });
    }

    try {
      await GoogleReviewService.postAppGoogleReviewPublish({
        reviewId,
        locationId,
        text,
      });
      return { reviewId, text };
    } catch (e) {
      return rejectGoogleReviewError(e, rejectWithValue);
    }
  },
);

export const bulkGenerateAiReplies = createAsyncThunk<
  Record<string, string>,
  string[],
  { rejectValue: GoogleReviewRejectPayload }
>(
  'googleReview/bulkGenerateAiReplies',
  async (reviewIds: string[], { getState, rejectWithValue }) => {
    const locationId = (getState() as RootState).googleReview.selectedLocationId;
    if (!locationId) {
      return rejectWithValue({ message: 'No location selected' });
    }

    try {
      const result = await GoogleReviewService.postAppGoogleReviewBulkGenerate({
        locationId,
        reviewIds,
      });
      return result.drafts ?? {};
    } catch (e) {
      return rejectGoogleReviewError(e, rejectWithValue);
    }
  },
);

export const bulkPublishReplies = createAsyncThunk<
  GoogleReviewBulkPublishResultView,
  { reviewId: string; text: string }[],
  { rejectValue: GoogleReviewRejectPayload }
>(
  'googleReview/bulkPublishReplies',
  async (items: { reviewId: string; text: string }[], { rejectWithValue }) => {
    try {
      return await GoogleReviewService.postAppGoogleReviewBulkPublish({ items });
    } catch (e) {
      return rejectGoogleReviewError(e, rejectWithValue);
    }
  },
);

function handleRejectedGoogleReviewAction(
  state: GoogleReviewState,
  action: { payload: unknown; error: { message?: string } },
) {
  const payload = action.payload as GoogleReviewRejectPayload | undefined;
  state.error = payload?.message ?? action.error.message ?? 'Unknown error';
  state.errorCode = payload?.errorCode ?? null;

  if (payload?.errorCode === 'google_token_expired') {
    state.needsReconnect = true;
    state.isConnected = false;
  }
}

const googleReviewSlice = createSlice({
  name: 'googleReview',
  initialState,
  reducers: {
    setActiveTab(state, action: PayloadAction<GoogleReviewState['activeTab']>) {
      state.activeTab = action.payload;
    },
    setRatingFilter(state, action: PayloadAction<number | null>) {
      state.ratingFilter = action.payload;
    },
    setIncludeRatingOnly(state, action: PayloadAction<boolean>) {
      state.includeRatingOnly = action.payload;
    },
    setSelectedAccountId(state, action: PayloadAction<string | null>) {
      state.selectedAccountId = action.payload;
      state.selectedLocationId = null;
      state.locations = [];
      state.reviews = [];
      state.counts = EMPTY_COUNTS;
      state.total = 0;
    },
    setSelectedLocationId(state, action: PayloadAction<string | null>) {
      state.selectedLocationId = action.payload;
    },
    toggleReviewSelection(state, action: PayloadAction<string>) {
      const id = action.payload;
      const idx = state.selectedReviewIds.indexOf(id);
      if (idx === -1) {
        if (state.selectedReviewIds.length >= BULK_GENERATE_MAX) return;
        state.selectedReviewIds.push(id);
      } else {
        state.selectedReviewIds.splice(idx, 1);
      }
    },
    selectAllReviews(state) {
      // `state.reviews` accumulates every loaded page, so this must be capped.
      state.selectedReviewIds = state.reviews
        .map((r) => r.reviewId)
        .filter((id): id is string => Boolean(id))
        .slice(0, BULK_GENERATE_MAX);
    },
    clearSelection(state) {
      state.selectedReviewIds = [];
    },
    openEditModal(state, action: PayloadAction<GoogleReviewView>) {
      state.currentEditReview = action.payload;
      state.currentEditDraftText = action.payload.draftText ?? action.payload.existingReply ?? '';
    },
    closeEditModal(state) {
      state.currentEditReview = null;
      state.currentEditDraftText = '';
    },
    setEditDraftText(state, action: PayloadAction<string>) {
      state.currentEditDraftText = action.payload;
    },
    updateBulkDraft(state, action: PayloadAction<{ reviewId: string; text: string }>) {
      state.bulkDrafts[action.payload.reviewId] = action.payload.text;
    },
    clearBulkDrafts(state) {
      state.bulkDrafts = {};
      state.selectedReviewIds = [];
    },
    clearError(state) {
      state.error = null;
      state.errorCode = null;
    },
    clearNeedsReconnect(state) {
      state.needsReconnect = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConnectionStatus.fulfilled, (state, action) => {
        state.isConnected = action.payload.isConnected ?? false;
        state.accountEmail = action.payload.accountEmail ?? null;
        applySyncState(state, action.payload.syncState);
      })
      .addCase(fetchAccounts.fulfilled, (state, action) => {
        state.accounts = action.payload;
      })
      .addCase(fetchAccounts.rejected, (state, action) => {
        handleRejectedGoogleReviewAction(state, action);
      })
      .addCase(fetchLocations.fulfilled, (state, action) => {
        state.locations = action.payload;
      })
      .addCase(fetchLocations.rejected, (state, action) => {
        handleRejectedGoogleReviewAction(state, action);
      })
      .addCase(fetchReviews.pending, (state, action) => {
        // A "Load more" (page > 1) keeps the current list visible under its own spinner.
        if ((action.meta.arg.page ?? 1) > 1) {
          state.isLoadingMore = true;
        } else {
          state.isLoading = true;
        }
        state.error = null;
      })
      .addCase(fetchReviews.fulfilled, (state, action) => {
        const requestedPage = action.meta.arg.page ?? 1;
        state.isLoading = false;
        state.isLoadingMore = false;

        const incoming = action.payload.reviews ?? [];
        state.reviews = requestedPage > 1 ? state.reviews.concat(incoming) : incoming;
        state.page = requestedPage;
        state.counts = {
          all: action.payload.counts?.all ?? 0,
          needsReply: action.payload.counts?.needsReply ?? 0,
          draft: action.payload.counts?.draft ?? 0,
          published: action.payload.counts?.published ?? 0,
        };
        state.total = action.payload.total ?? 0;
        applySyncState(state, action.payload.syncState);
      })
      .addCase(fetchReviews.rejected, (state, action) => {
        state.isLoading = false;
        state.isLoadingMore = false;
        handleRejectedGoogleReviewAction(state, action);
      })
      .addCase(triggerSync.pending, (state) => {
        state.isSyncTriggering = true;
      })
      .addCase(triggerSync.fulfilled, (state, action) => {
        state.isSyncTriggering = false;
        applySyncState(state, action.payload);
      })
      .addCase(triggerSync.rejected, (state, action) => {
        state.isSyncTriggering = false;
        handleRejectedGoogleReviewAction(state, action);
      })
      .addCase(generateAiReply.pending, (state) => {
        state.isGenerating = true;
      })
      .addCase(generateAiReply.fulfilled, (state, action) => {
        state.isGenerating = false;
        state.currentEditDraftText = action.payload.text;
      })
      .addCase(generateAiReply.rejected, (state, action) => {
        state.isGenerating = false;
        handleRejectedGoogleReviewAction(state, action);
      })
      .addCase(saveDraft.fulfilled, (state, action) => {
        const review = state.reviews.find((r) => r.reviewId === action.payload.reviewId);
        if (review) {
          review.status = GoogleReviewViewModel.status.DRAFT;
          review.draftText = action.payload.text;
        }
        state.currentEditReview = null;
        state.currentEditDraftText = '';
      })
      .addCase(saveDraft.rejected, (state, action) => {
        handleRejectedGoogleReviewAction(state, action);
      })
      .addCase(publishReply.pending, (state) => {
        state.isPublishing = true;
      })
      .addCase(publishReply.fulfilled, (state, action) => {
        state.isPublishing = false;
        const review = state.reviews.find((r) => r.reviewId === action.payload.reviewId);
        if (review) {
          review.status = GoogleReviewViewModel.status.PUBLISHED;
          review.existingReply = action.payload.text;
          review.draftText = undefined;
        }
        state.currentEditReview = null;
        state.currentEditDraftText = '';
      })
      .addCase(publishReply.rejected, (state, action) => {
        state.isPublishing = false;
        handleRejectedGoogleReviewAction(state, action);
      })
      .addCase(bulkGenerateAiReplies.pending, (state) => {
        state.isGenerating = true;
      })
      .addCase(bulkGenerateAiReplies.fulfilled, (state, action) => {
        state.isGenerating = false;
        state.bulkDrafts = action.payload;
      })
      .addCase(bulkGenerateAiReplies.rejected, (state, action) => {
        state.isGenerating = false;
        handleRejectedGoogleReviewAction(state, action);
      })
      .addCase(bulkPublishReplies.pending, (state) => {
        state.isPublishing = true;
      })
      .addCase(bulkPublishReplies.fulfilled, (state, action) => {
        state.isPublishing = false;
        const items = action.meta.arg as { reviewId: string; text: string }[];
        items.forEach(({ reviewId, text }) => {
          const review = state.reviews.find((r) => r.reviewId === reviewId);
          if (review) {
            review.status = GoogleReviewViewModel.status.PUBLISHED;
            review.existingReply = text;
            review.draftText = undefined;
          }
        });
      })
      .addCase(bulkPublishReplies.rejected, (state, action) => {
        state.isPublishing = false;
        handleRejectedGoogleReviewAction(state, action);
      });
  },
});

export const {
  setActiveTab,
  setRatingFilter,
  setIncludeRatingOnly,
  setSelectedAccountId,
  setSelectedLocationId,
  toggleReviewSelection,
  selectAllReviews,
  clearSelection,
  openEditModal,
  closeEditModal,
  setEditDraftText,
  updateBulkDraft,
  clearBulkDrafts,
  clearError,
  clearNeedsReconnect,
} = googleReviewSlice.actions;

export default googleReviewSlice.reducer;
